// API functions for questionnaire operations
// src/api/questionnaire.ts

export interface QuestionnaireQuestion {
  id: number;
  text: string; // Backend returns language-specific text directly
  type: 'likert' | 'binary' | 'text' | 'yes_no';
  required: boolean;
  choices?: {
    id: number;
    text: string; // Backend returns language-specific text directly
    value: number;
  }[];
}

export interface QuestionnaireResponse {
  patientId: string;
  questionnaireId: string;
  answers: Record<number, any>;
  startedAt?: Date | null;
  completedAt: Date;
}

// Configure axios base URL to point to your FastAPI backend
// Use same-origin (empty string) for all requests in production
// Development mode will use localhost through proxy in package.json
const API_BASE_URL = '';

// Export for use in fetch calls
export const API_BASE = API_BASE_URL;

export async function getQuestionnaireQuestions(questionnaireId: string, language: 'en' | 'el' = 'el'): Promise<QuestionnaireQuestion[]> {
  // Map frontend language codes to backend language codes
  const backendLang = language === 'el' ? 'greek' : 'english';
  const url = `${API_BASE}/api/questionnaires/${questionnaireId}/questions?lang=${backendLang}`;
  
  console.log(`Fetching questions from: ${url}`);
  
  const response = await fetch(url, {
    credentials: 'include' // Include cookies for session authentication
  });
  console.log(`Response status: ${response.status}`);
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error(`API Error: ${response.status} - ${errorText}`);
    throw new Error(`Failed to fetch questionnaire questions: ${response.statusText}`);
  }
  
  const result = await response.json();
  console.log('API Response:', result);
  
  // The backend returns a StandardResponse with data containing the questions
  if (result.status_code === 200 && result.data && result.data.questions) {
    return result.data.questions;
  }
  
  console.error('Invalid response format:', result);
  throw new Error('Invalid response format from backend');
}

export async function getPatientQuestionnaireAnswers(
  patientId: string,
  questionnaireId: string
): Promise<Record<number, any>> {
  const url = `${API_BASE}/patients/${patientId}/questionnaires/${questionnaireId}/status`;
  
  console.log(`Loading existing answers for patient ${patientId}, questionnaire ${questionnaireId}`);
  
  try {
    const response = await fetch(url, {
      credentials: 'include' // Include cookies for session authentication
    });
    if (!response.ok) {
      console.log('No existing answers found or failed to fetch');
      return {};
    }
    
    const result = await response.json();
    
    // Extract answers from the StandardResponse format
    if (result.status_code === 200 && result.data && result.data.answers) {
      console.log('Loaded existing answers:', result.data.answers);
      
      // Convert string keys to numbers for consistency with frontend
      const convertedAnswers: Record<number, any> = {};
      for (const [key, value] of Object.entries(result.data.answers)) {
        convertedAnswers[parseInt(key)] = value;
      }
      
      return convertedAnswers;
    }
    
    return {};
  } catch (error) {
    console.log('Failed to load existing answers:', error);
    return {};
  }
}

export async function submitSingleAnswer(
  patientId: string,
  questionnaireId: string, 
  questionId: number,
  answer: any
): Promise<void> {
  const url = `${API_BASE}/api/questionnaires/${questionnaireId}/answer`;
  
  console.log(`Auto-saving answer: Patient ${patientId}, Question ${questionId}, Answer:`, answer);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for session authentication
    body: JSON.stringify({
      patient_id: patientId,
      question_id: questionId,
      answer: answer
    }),
  });
  
  if (!response.ok) {
    console.error(`Failed to auto-save answer for question ${questionId}:`, response.statusText);
    throw new Error(`Failed to auto-save answer: ${response.statusText}`);
  }
  
  console.log(`✓ Auto-saved answer for question ${questionId}`);
}

export async function submitQuestionnaireResponse(response: QuestionnaireResponse): Promise<void> {
  const result = await fetch(`${API_BASE}/api/questionnaires/${response.questionnaireId}/submit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for session authentication
    body: JSON.stringify({
      patient_id: response.patientId,
      answers: Object.entries(response.answers).map(([questionId, answer]) => ({
        question_id: parseInt(questionId),
        answer: answer
      })),
      started_at: response.startedAt?.toISOString(),
      completed_at: response.completedAt.toISOString()
    }),
  });
  
  if (!result.ok) {
    throw new Error(`Failed to submit questionnaire response: ${result.statusText}`);
  }
}

export interface TokenQuestionnaireResponse extends Omit<QuestionnaireResponse, 'patientId'> {
  displayToken: string;
}

export async function submitQuestionnaireResponseViaToken(response: TokenQuestionnaireResponse): Promise<void> {
  const requestBody = Object.entries(response.answers).map(([questionId, answer]) => ({
    question_id: parseInt(questionId),
    answer: answer
  }));
  
  const result = await fetch(`${API_BASE}/questionnaire/${response.displayToken}/submit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for session authentication
    body: JSON.stringify(requestBody),
  });
  
  if (!result.ok) {
    // Try to extract backend error details for better diagnostics
    let backendMessage = result.statusText;
    try {
      const data = await result.json();
      if (data && (data.message || data.detail)) {
        backendMessage = data.message || data.detail;
      }
    } catch (_) {
      // ignore JSON parsing errors
    }
    throw new Error(`Failed to submit questionnaire response via token: ${backendMessage}`);
  }
}

export async function getPatientQuestionnaireStatus(patientId: string, questionnaireId: string): Promise<{
  isCompleted: boolean;
  completedAt?: Date;
}> {
  const response = await fetch(`${API_BASE}/patients/${patientId}/questionnaires/${questionnaireId}/status`);
  if (!response.ok) {
    throw new Error(`Failed to fetch questionnaire status: ${response.statusText}`);
  }
  return response.json();
}

// GDPR Consent API functions
export interface GDPRConsentRequest {
  patient_id: string;
  questionnaire_id: string;
  consent_given: boolean;
}

export interface GDPRConsentResponse {
  patient_id: string;
  questionnaire_id: string;
  has_consent: boolean;
  consent_date?: string;
}

export async function storeGDPRConsent(
  patientId: string,
  questionnaireId: string,
  consentGiven: boolean
): Promise<GDPRConsentResponse> {
  const url = `${API_BASE}/api/gdpr/consent`;
  
  console.log(`Storing GDPR consent: Patient ${patientId}, Questionnaire ${questionnaireId}, Consent: ${consentGiven}`);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for session authentication
    body: JSON.stringify({
      patient_id: patientId,
      questionnaire_id: questionnaireId,
      consent_given: consentGiven
    }),
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error(`Failed to store GDPR consent: ${response.status} - ${errorText}`);
    throw new Error(`Failed to store GDPR consent: ${response.statusText}`);
  }
  
  const result = await response.json();
  console.log('GDPR consent stored successfully:', result);
  
  if (result.status_code === 201 && result.data) {
    return result.data;
  }
  
  throw new Error('Invalid response format from GDPR consent API');
}

export async function getGDPRConsent(
  patientId: string,
  questionnaireId: string
): Promise<GDPRConsentResponse | null> {
  const url = `${API_BASE}/api/gdpr/consent/${patientId}/${questionnaireId}`;
  
  console.log(`Checking GDPR consent: Patient ${patientId}, Questionnaire ${questionnaireId}`);
  
  try {
    const response = await fetch(url, {
      credentials: 'include' // Include cookies for session authentication
    });
    
    if (response.status === 404) {
      console.log('No GDPR consent found');
      return null;
    }
    
    if (!response.ok) {
      console.error(`Failed to get GDPR consent: ${response.statusText}`);
      return null;
    }
    
    const result = await response.json();
    console.log('GDPR consent status:', result);
    
    if (result.status_code === 200 && result.data) {
      return result.data;
    }
    
    return null;
  } catch (error) {
    console.error('Error checking GDPR consent:', error);
    return null;
  }
}